import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon paths (CRA/webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const lastIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#D4AF37;border:3px solid #050505;box-shadow:0 0 0 2px #D4AF37, 0 0 14px rgba(212,175,55,0.8);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const dotIcon = L.divIcon({
  className: "",
  html: `<div style="width:10px;height:10px;border-radius:50%;background:#A1A1AA;border:2px solid #050505;"></div>`,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
    }
  }, [points, map]);
  return null;
}

export default function PingMap({ pings = [], height = 320, destination }) {
  // Defer mount one tick so StrictMode's double-invoke doesn't trip "container already initialized"
  const [mounted, setMounted] = useState(false);
  const keyRef = useRef(`map-${Math.random().toString(36).slice(2)}`);
  useEffect(() => { setMounted(true); }, []);

  const center = pings.length > 0
    ? [pings[pings.length - 1].lat, pings[pings.length - 1].lng]
    : destination
      ? [destination.lat, destination.lng]
      : [3.139, 101.6869]; // KL fallback
  const zoom = pings.length > 0 ? 14 : 11;
  const polyline = pings.map((p) => [p.lat, p.lng]);

  if (!mounted) {
    return <div className="rounded-sm border border-[#1a1a1a] bg-[#0a0a0a] flex items-center justify-center text-zinc-600 text-xs" style={{ height }}>Loading map…</div>;
  }

  return (
    <div className="rounded-sm overflow-hidden border border-[#1a1a1a] relative" style={{ height }}>
      <MapContainer
        key={keyRef.current}
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
        />
        {pings.length > 0 && <FitBounds points={pings} />}
        {pings.length > 1 && (
          <Polyline positions={polyline} pathOptions={{ color: "#D4AF37", weight: 3, opacity: 0.7, dashArray: "6 6" }} />
        )}
        {pings.map((p, i) => (
          <Marker key={i} position={[p.lat, p.lng]} icon={i === pings.length - 1 ? lastIcon : dotIcon}>
            <Popup>
              <div style={{ color: "#050505" }}>
                <div style={{ fontWeight: 600 }}>{i === pings.length - 1 ? "Latest position" : `Ping #${i + 1}`}</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>{new Date(p.at).toLocaleString()}</div>
                {p.accuracy && <div style={{ fontSize: 11, color: "#666" }}>±{Math.round(p.accuracy)}m</div>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {pings.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-[#0a0a0a]/85 backdrop-blur-sm border border-[#2a2a2a] rounded-sm px-4 py-2 text-xs text-zinc-400">
            No GPS pings yet · tap "Ping Location" below
          </div>
        </div>
      )}
    </div>
  );
}
